import { logger } from '@logger'
import { connectSocket, disconnectSocket } from './socketConnection'
import { registerListeners, removeListeners } from './socketListeners'
import {
  emitPlaceBet,
  emitCashOut,
  emitJoinRoom,
  emitLeaveRoom,
  emitGameStats,
  emitLastMultipliers,
} from './socketEmitters'

const MODULE = 'Socket Service'

/* ─────────────────────────── Orchestration ──────────────────────────────────
 * Used by the bootstrap hook. `startSocket` opens the singleton and binds the
 * listeners; `stopSocket` unbinds and tears it down.
 * ───────────────────────────────────────────────────────────────────────── */
export const startSocket = ({ accessToken, gameCode, gameMode } = {}) => {
  logger.info(MODULE, 'Starting socket', { gameCode, gameMode })
  const socket = connectSocket({ accessToken })
  if (!socket) return null
  registerListeners({ gameCode, gameMode })
  return socket
}

export const stopSocket = () => {
  logger.info(MODULE, 'Stopping socket')
  removeListeners()
  disconnectSocket()
}

/* ────────────────────────── High-level actions ──────────────────────────────
 * The public API the app calls. Add validation / business rules here; the
 * actual wire-format goes through the emitters. All are fire-and-forget — the
 * server's reply arrives as a separate event handled in socketListeners.
 * ───────────────────────────────────────────────────────────────────────── */

/**
 * @param {object} bet
 * @param {number}  bet.betAmount  required (> 0). For FREEBET this is the stake.
 * @param {string}  bet.currency   required
 * @param {number} [bet.cashOutAt] 0/omit = manual; > 1 = auto-cashout target
 * @param {number} [bet.panel]     FE panel id (e.g. dual-bet UI 1 or 2)
 * @param {string} [bet.subType]   MANUAL (default) | AUTO | FREEBET
 * @param {string} [bet.source]    required for FREEBET: PURCHASED|MISSION|TOURNAMENT|JACKPOT|RAIN
 * @param {string} [bet.freebetId] required when subType = FREEBET
 * @param {string} [bet.sourceId]  optional freebet correlation id
 * @param {number} [bet.jackpotRatio]
 * @param {string} [bet.gameCode]  defaults server-side to the token's gameCode
 */
export const placeBet = ({
  betAmount,
  currency,
  cashOutAt = 0,
  panel,
  subType = 'MANUAL',
  source = null,
  freebetId = null,
  sourceId = null,
  jackpotRatio = null,
  gameCode,
} = {}) => {
  if (!betAmount || betAmount <= 0) {
    logger.error(MODULE, 'placeBet: betAmount must be > 0', betAmount)
    return false
  }
  if (!currency) {
    logger.error(MODULE, 'placeBet: currency is required')
    return false
  }
  return emitPlaceBet({
    betAmount,
    currency,
    cashOutAt,
    panel,
    subType,
    source,
    freebetId,
    sourceId,
    jackpotRatio,
    gameCode,
  })
}

export const cashOut = ({ transactionId, gameCode } = {}) => {
  if (!transactionId) {
    logger.error(MODULE, 'cashOut: transactionId is required')
    return false
  }
  return emitCashOut({ transactionId, gameCode })
}

export const joinRoom = (room) => emitJoinRoom(room)
export const leaveRoom = (room) => emitLeaveRoom(room)

// Re-request the bootstrap snapshots on demand.
export const refreshGameStats = () => emitGameStats()
export const refreshLastMultipliers = () => emitLastMultipliers()
